// =============================================================================
// Edict Structured Errors
// =============================================================================
// Every compiler error is a structured JSON object with enough context
// for an agent to self-repair. No human-readable strings.

import type { Effect } from "../ast/nodes.js";
import type { TypeExpr } from "../ast/types.js";

/**
 * A concrete AST patch an agent can apply to fix an error.
 * `nodeId` identifies the node to patch, `field` is the field to change,
 * and `value` is the new value for that field.
 */
export interface FixSuggestion {
    nodeId: string | null;
    field: string;
    value: unknown;
}

/**
 * Union of all compiler errors (Phase 1–4).
 */
export type StructuredError =
    // Phase 1 — validation errors
    | DuplicateIdError
    | UnknownNodeKindError
    | MissingFieldError
    | InvalidFieldTypeError
    | InvalidEffectError
    | InvalidOperatorError
    | InvalidBasicTypeName
    | ConflictingEffectsError
    // Phase 2 — name resolution errors
    | UndefinedReferenceError
    | DuplicateDefinitionError
    | UnknownRecordError
    | UnknownEnumError
    | UnknownVariantError
    // Phase 2 — type checking errors
    | TypeMismatchError
    | UnitMismatchError
    | ArityMismatchError
    | NotAFunctionError
    | UnknownFieldError
    | MissingRecordFieldsError
    // Phase 2c — complexity checking errors
    | FunctionComplexityExceededError
    | ModuleComplexityExceededError
    // Phase 3 — effect checking errors
    | EffectViolationError
    | EffectInPureError
    // Phase 4 — contract verification errors
    | ContractFailureError
    | VerificationTimeoutError
    | UndecidablePredicateError
    | PreconditionNotMetError
    | InvalidSemanticAssertionError
    // Patch errors
    | PatchNodeNotFoundError
    | PatchInvalidFieldError
    | PatchIndexOutOfRangeError
    | PatchDeleteNotInArrayError
    // Phase 5 — Codegen errors
    | WasmValidationError
    | MissingEntryPointError
    // Composition errors
    | UnsatisfiedRequirementError
    | DuplicateProvisionError
    // Multi-module errors
    | CircularImportError
    | UnresolvedModuleError
    | DuplicateModuleNameError
    // Runtime errors
    | MissingExternalModuleError
    // Migration errors
    | MigrationFailedError
    | UnsupportedSchemaVersionError
    // Capability errors
    | CapabilityMissingError
    // Approval errors
    | ApprovalPropagationMissingError
    // Tool errors
    | UnknownToolError
    | ToolArgMismatchError
    // Deploy errors
    | ScaffoldFailedError
    | DeployFailedError
    | UnknownDeployTargetError
    // QuickJS runtime errors
    | QuickJSRuntimeError;

// =============================================================================
// Phase 1 — Validation errors
// =============================================================================

export interface DuplicateIdError {
    error: "duplicate_id";
    nodeId: string;
    firstPath: string;
    secondPath: string;
}

export interface UnknownNodeKindError {
    error: "unknown_node_kind";
    path: string;
    received: string;
    validKinds: readonly string[];
    suggestion?: FixSuggestion;
}

export interface MissingFieldError {
    error: "missing_field";
    path: string;
    nodeId: string | null;
    field: string;
    expectedFormat: string;
    hint: string;
    suggestion?: FixSuggestion;
}

export interface InvalidFieldTypeError {
    error: "invalid_field_type";
    path: string;
    nodeId: string | null;
    field: string;
    expectedFormat: string;
    actualFormat: string;
}

export interface InvalidEffectError {
    error: "invalid_effect";
    path: string;
    nodeId: string | null;
    received: string;
    validEffects: readonly string[];
}

export interface InvalidOperatorError {
    error: "invalid_operator";
    path: string;
    nodeId: string | null;
    received: string;
    validOperators: readonly string[];
}

export interface InvalidBasicTypeName {
    error: "invalid_basic_type_name";
    path: string;
    nodeId: string | null;
    received: string;
    validNames: readonly string[];
}

export interface ConflictingEffectsError {
    error: "conflicting_effects";
    path: string;
    nodeId: string | null;
    effectsFound: string[];
}

// =============================================================================
// Phase 2 — Name resolution errors
// =============================================================================

export interface UndefinedReferenceError {
    error: "undefined_reference";
    nodeId: string | null;
    name: string;
    candidates: string[];
    suggestion?: FixSuggestion;
}

export interface DuplicateDefinitionError {
    error: "duplicate_definition";
    nodeId: string | null;
    name: string;
    firstNodeId: string | null;
}

export interface UnknownRecordError {
    error: "unknown_record";
    nodeId: string | null;
    name: string;
    candidates: string[];
    suggestion?: FixSuggestion;
}

export interface UnknownEnumError {
    error: "unknown_enum";
    nodeId: string | null;
    name: string;
    candidates: string[];
    suggestion?: FixSuggestion;
}

export interface UnknownVariantError {
    error: "unknown_variant";
    nodeId: string | null;
    enumName: string;
    variantName: string;
    availableVariants: string[];
    suggestion?: FixSuggestion;
}

// =============================================================================
// Phase 2 — Type checking errors
// =============================================================================

export interface TypeMismatchError {
    error: "type_mismatch";
    nodeId: string | null;
    expected: TypeExpr;
    actual: TypeExpr;
    suggestion?: FixSuggestion;
}

export interface UnitMismatchError {
    error: "unit_mismatch";
    nodeId: string | null;
    expectedUnit: string;
    actualUnit: string;
    expectedBase: "Int" | "Float";
    actualBase: "Int" | "Float";
}

export interface ArityMismatchError {
    error: "arity_mismatch";
    nodeId: string | null;
    expected: number;
    actual: number;
}

export interface NotAFunctionError {
    error: "not_a_function";
    nodeId: string | null;
    actualType: TypeExpr;
}

export interface UnknownFieldError {
    error: "unknown_field";
    nodeId: string | null;
    recordName: string;
    fieldName: string;
    availableFields: string[];
    suggestion?: FixSuggestion;
}

export interface MissingRecordFieldsError {
    error: "missing_record_fields";
    nodeId: string | null;
    recordName: string;
    missingFields: string[];
    suggestion?: FixSuggestion;
}

// =============================================================================
// Phase 1 error constructors
// =============================================================================

export function duplicateId(
    nodeId: string,
    firstPath: string,
    secondPath: string,
): DuplicateIdError {
    return { error: "duplicate_id", nodeId, firstPath, secondPath };
}

export function unknownNodeKind(
    path: string,
    received: string,
    validKinds: readonly string[],
    suggestion?: FixSuggestion,
): UnknownNodeKindError {
    const result: UnknownNodeKindError = { error: "unknown_node_kind", path, received, validKinds };
    if (suggestion) result.suggestion = suggestion;
    return result;
}

export function missingField(
    path: string,
    nodeId: string | null,
    field: string,
    expectedFormat: string,
    suggestion?: FixSuggestion,
): MissingFieldError {
    const result: MissingFieldError = { error: "missing_field", path, nodeId, field, expectedFormat, hint: "use edict_explain for repair context" };
    if (suggestion) result.suggestion = suggestion;
    return result;
}

export function invalidFieldType(
    path: string,
    nodeId: string | null,
    field: string,
    expectedFormat: string,
    actualFormat: string,
): InvalidFieldTypeError {
    return { error: "invalid_field_type", path, nodeId, field, expectedFormat, actualFormat };
}

export function invalidEffect(
    path: string,
    nodeId: string | null,
    received: string,
    validEffects: readonly string[],
): InvalidEffectError {
    return { error: "invalid_effect", path, nodeId, received, validEffects };
}

export function invalidOperator(
    path: string,
    nodeId: string | null,
    received: string,
    validOperators: readonly string[],
): InvalidOperatorError {
    return {
        error: "invalid_operator",
        path,
        nodeId,
        received,
        validOperators,
    };
}

export function invalidBasicTypeName(
    path: string,
    nodeId: string | null,
    received: string,
    validNames: readonly string[],
): InvalidBasicTypeName {
    return { error: "invalid_basic_type_name", path, nodeId, received, validNames };
}

export function conflictingEffects(
    path: string,
    nodeId: string | null,
    effectsFound: string[],
): ConflictingEffectsError {
    return { error: "conflicting_effects", path, nodeId, effectsFound };
}

// =============================================================================
// Phase 2 error constructors
// =============================================================================

export function undefinedReference(
    nodeId: string | null,
    name: string,
    candidates: string[],
    suggestion?: FixSuggestion,
): UndefinedReferenceError {
    const err: UndefinedReferenceError = { error: "undefined_reference", nodeId, name, candidates };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function duplicateDefinition(
    nodeId: string | null,
    name: string,
    firstNodeId: string | null,
): DuplicateDefinitionError {
    return { error: "duplicate_definition", nodeId, name, firstNodeId };
}

export function unknownRecord(
    nodeId: string | null,
    name: string,
    candidates: string[],
    suggestion?: FixSuggestion,
): UnknownRecordError {
    const err: UnknownRecordError = { error: "unknown_record", nodeId, name, candidates };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function unknownEnum(
    nodeId: string | null,
    name: string,
    candidates: string[],
    suggestion?: FixSuggestion,
): UnknownEnumError {
    const err: UnknownEnumError = { error: "unknown_enum", nodeId, name, candidates };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function unknownVariant(
    nodeId: string | null,
    enumName: string,
    variantName: string,
    availableVariants: string[],
    suggestion?: FixSuggestion,
): UnknownVariantError {
    const err: UnknownVariantError = { error: "unknown_variant", nodeId, enumName, variantName, availableVariants };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function typeMismatch(
    nodeId: string | null,
    expected: TypeExpr,
    actual: TypeExpr,
    suggestion?: FixSuggestion,
): TypeMismatchError {
    const err: TypeMismatchError = { error: "type_mismatch", nodeId, expected, actual };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function unitMismatch(
    nodeId: string | null,
    expectedUnit: string,
    actualUnit: string,
    expectedBase: "Int" | "Float",
    actualBase: "Int" | "Float",
): UnitMismatchError {
    return { error: "unit_mismatch", nodeId, expectedUnit, actualUnit, expectedBase, actualBase };
}

export function arityMismatch(
    nodeId: string | null,
    expected: number,
    actual: number,
): ArityMismatchError {
    return { error: "arity_mismatch", nodeId, expected, actual };
}

export function notAFunction(
    nodeId: string | null,
    actualType: TypeExpr,
): NotAFunctionError {
    return { error: "not_a_function", nodeId, actualType };
}

export function unknownField(
    nodeId: string | null,
    recordName: string,
    fieldName: string,
    availableFields: string[],
    suggestion?: FixSuggestion,
): UnknownFieldError {
    const err: UnknownFieldError = { error: "unknown_field", nodeId, recordName, fieldName, availableFields };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function missingRecordFields(
    nodeId: string | null,
    recordName: string,
    missingFields: string[],
    suggestion?: FixSuggestion,
): MissingRecordFieldsError {
    const err: MissingRecordFieldsError = { error: "missing_record_fields", nodeId, recordName, missingFields };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

// =============================================================================
// Phase 2c — Complexity checking errors
// =============================================================================

export interface FunctionComplexityExceededError {
    error: "function_complexity_exceeded";
    nodeId: string;
    functionName: string;
    metric: "maxAstNodes" | "maxCallDepth" | "maxBranches";
    actual: number;
    limit: number;
}

export interface ModuleComplexityExceededError {
    error: "module_complexity_exceeded";
    metric: "maxAstNodes" | "maxCallDepth" | "maxBranches";
    actual: number;
    limit: number;
}

// =============================================================================
// Phase 2c error constructors
// =============================================================================

export function functionComplexityExceeded(
    nodeId: string,
    functionName: string,
    metric: "maxAstNodes" | "maxCallDepth" | "maxBranches",
    actual: number,
    limit: number,
): FunctionComplexityExceededError {
    return { error: "function_complexity_exceeded", nodeId, functionName, metric, actual, limit };
}

export function moduleComplexityExceeded(
    metric: "maxAstNodes" | "maxCallDepth" | "maxBranches",
    actual: number,
    limit: number,
): ModuleComplexityExceededError {
    return { error: "module_complexity_exceeded", metric, actual, limit };
}

// =============================================================================
// Phase 3 — Effect checking errors
// =============================================================================

export interface EffectViolationError {
    error: "effect_violation";
    nodeId: string | null;
    functionName: string;
    missingEffects: Effect[];
    callSiteNodeId: string | null;
    calleeName: string;
    suggestion?: FixSuggestion;
}

export interface EffectInPureError {
    error: "effect_in_pure";
    nodeId: string | null;
    functionName: string;
    callSiteNodeId: string | null;
    calleeName: string;
    calleeEffects: Effect[];
    suggestion?: FixSuggestion;
}

// =============================================================================
// Phase 3 error constructors
// =============================================================================

export function effectViolation(
    nodeId: string | null,
    functionName: string,
    missingEffects: Effect[],
    callSiteNodeId: string | null,
    calleeName: string,
    suggestion?: FixSuggestion,
): EffectViolationError {
    const err: EffectViolationError = { error: "effect_violation", nodeId, functionName, missingEffects, callSiteNodeId, calleeName };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

export function effectInPure(
    nodeId: string | null,
    functionName: string,
    callSiteNodeId: string | null,
    calleeName: string,
    calleeEffects: Effect[],
    suggestion?: FixSuggestion,
): EffectInPureError {
    const err: EffectInPureError = { error: "effect_in_pure", nodeId, functionName, callSiteNodeId, calleeName, calleeEffects };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

// =============================================================================
// Phase 4 — Contract verification errors
// =============================================================================

export interface ContractFailureError {
    error: "contract_failure";
    nodeId: string;
    contractId: string;
    functionName: string;
    contractKind: "pre" | "post";
    counterexample: Record<string, unknown>;
    semanticAssertion?: string;
}

export interface VerificationTimeoutError {
    error: "verification_timeout";
    nodeId: string;
    contractId: string;
    functionName: string;
    timeoutMs: number;
}

export interface UndecidablePredicateError {
    error: "undecidable_predicate";
    nodeId: string;
    contractId: string;
    functionName: string;
    unsupportedNodeKind: string;
}

export interface PreconditionNotMetError {
    error: "precondition_not_met";
    nodeId: string;
    callSiteId: string;
    callerName: string;
    calleeName: string;
    contractId: string;
    counterexample: Record<string, unknown>;
}

export interface InvalidSemanticAssertionError {
    error: "invalid_semantic_assertion";
    nodeId: string;
    contractId: string;
    received: string;
    validAssertions: readonly string[];
}

// =============================================================================
// Phase 4 error constructors
// =============================================================================

export function contractFailure(
    nodeId: string,
    contractId: string,
    functionName: string,
    contractKind: "pre" | "post",
    counterexample: Record<string, unknown>,
    semanticAssertion?: string,
): ContractFailureError {
    const err: ContractFailureError = { error: "contract_failure", nodeId, contractId, functionName, contractKind, counterexample };
    if (semanticAssertion) err.semanticAssertion = semanticAssertion;
    return err;
}

export function verificationTimeout(
    nodeId: string,
    contractId: string,
    functionName: string,
    timeoutMs: number,
): VerificationTimeoutError {
    return { error: "verification_timeout", nodeId, contractId, functionName, timeoutMs };
}

export function undecidablePredicate(
    nodeId: string,
    contractId: string,
    functionName: string,
    unsupportedNodeKind: string,
): UndecidablePredicateError {
    return { error: "undecidable_predicate", nodeId, contractId, functionName, unsupportedNodeKind };
}

export function preconditionNotMet(
    nodeId: string,
    callSiteId: string,
    callerName: string,
    calleeName: string,
    contractId: string,
    counterexample: Record<string, unknown>,
): PreconditionNotMetError {
    return { error: "precondition_not_met", nodeId, callSiteId, callerName, calleeName, contractId, counterexample };
}

export function invalidSemanticAssertion(
    nodeId: string,
    contractId: string,
    received: string,
    validAssertions: readonly string[],
): InvalidSemanticAssertionError {
    return { error: "invalid_semantic_assertion", nodeId, contractId, received, validAssertions };
}

// =============================================================================
// Patch errors
// =============================================================================

export interface PatchNodeNotFoundError {
    error: "patch_node_not_found";
    nodeId: string | null;
    patchIndex: number;
}

export interface PatchInvalidFieldError {
    error: "patch_invalid_field";
    nodeId: string;
    field: string;
    availableFields: string[];
    patchIndex: number;
}

export interface PatchIndexOutOfRangeError {
    error: "patch_index_out_of_range";
    nodeId: string;
    field: string;
    index: number;
    arrayLength: number;
    patchIndex: number;
}

export interface PatchDeleteNotInArrayError {
    error: "patch_delete_not_in_array";
    nodeId: string;
    patchIndex: number;
}

// =============================================================================
// Patch error constructors
// =============================================================================

export function patchNodeNotFound(
    nodeId: string | null,
    patchIndex: number,
): PatchNodeNotFoundError {
    return { error: "patch_node_not_found", nodeId, patchIndex };
}

export function patchInvalidField(
    nodeId: string,
    field: string,
    availableFields: string[],
    patchIndex: number,
): PatchInvalidFieldError {
    return { error: "patch_invalid_field", nodeId, field, availableFields, patchIndex };
}

export function patchIndexOutOfRange(
    nodeId: string,
    field: string,
    index: number,
    arrayLength: number,
    patchIndex: number,
): PatchIndexOutOfRangeError {
    return { error: "patch_index_out_of_range", nodeId, field, index, arrayLength, patchIndex };
}

export function patchDeleteNotInArray(
    nodeId: string,
    patchIndex: number,
): PatchDeleteNotInArrayError {
    return { error: "patch_delete_not_in_array", nodeId, patchIndex };
}

// =============================================================================
// Analysis Diagnostics (INFO-level, not errors)
// =============================================================================
// These signal "not checked" rather than "check failed". An agent receiving
// diagnostics knows exactly which functions were verified vs. skipped.

export type AnalysisDiagnosticKind =
    | "effect_skipped_import"
    | "effect_skipped_unknown_callee"
    | "contract_skipped_unsupported_params";

export interface AnalysisDiagnostic {
    diagnostic: AnalysisDiagnosticKind;
    functionName: string;
    nodeId: string;
    phase: "effects" | "contracts";
    /** Additional context, e.g. the import name or unsupported param type */
    detail?: string;
}

export interface VerificationCoverage {
    effects: { checked: number; skipped: number; total: number };
    contracts: { proven: number; skipped: number; total: number };
}

export function analysisDiagnostic(
    diagnostic: AnalysisDiagnosticKind,
    functionName: string,
    nodeId: string,
    phase: "effects" | "contracts",
    detail?: string,
): AnalysisDiagnostic {
    const d: AnalysisDiagnostic = { diagnostic, functionName, nodeId, phase };
    if (detail !== undefined) d.detail = detail;
    return d;
}

// =============================================================================
// Phase 5 — Codegen errors
// =============================================================================

export interface WasmValidationError {
    error: "wasm_validation_error";
    message: string;
}

export interface MissingEntryPointError {
    error: "missing_entry_point";
    entryPointName: string;
}

export function wasmValidationError(
    message: string,
): WasmValidationError {
    return { error: "wasm_validation_error", message };
}

export function missingEntryPoint(
    entryPointName: string,
): MissingEntryPointError {
    return { error: "missing_entry_point", entryPointName };
}

// =============================================================================
// Composition errors
// =============================================================================

export interface UnsatisfiedRequirementError {
    error: "unsatisfied_requirement";
    fragmentId: string;
    requirement: string;
    availableProvisions: string[];
}

export interface DuplicateProvisionError {
    error: "duplicate_provision";
    name: string;
    fragmentIds: string[];
}

// =============================================================================
// Composition error constructors
// =============================================================================

export function unsatisfiedRequirement(
    fragmentId: string,
    requirement: string,
    availableProvisions: string[],
): UnsatisfiedRequirementError {
    return { error: "unsatisfied_requirement", fragmentId, requirement, availableProvisions };
}

export function duplicateProvision(
    name: string,
    fragmentIds: string[],
): DuplicateProvisionError {
    return { error: "duplicate_provision", name, fragmentIds };
}

// =============================================================================
// Multi-module errors
// =============================================================================

export interface CircularImportError {
    error: "circular_import";
    cycle: string[];
    nodeId: string | null;
}

export interface UnresolvedModuleError {
    error: "unresolved_module";
    moduleName: string;
    importNodeId: string;
    available: string[];
}

export interface DuplicateModuleNameError {
    error: "duplicate_module_name";
    moduleName: string;
    moduleIds: string[];
}

// =============================================================================
// Multi-module error constructors
// =============================================================================

export function circularImport(
    cycle: string[],
    nodeId: string | null = null,
): CircularImportError {
    return { error: "circular_import", cycle, nodeId };
}

export function unresolvedModule(
    moduleName: string,
    importNodeId: string,
    available: string[],
): UnresolvedModuleError {
    return { error: "unresolved_module", moduleName, importNodeId, available };
}

export function duplicateModuleName(
    moduleName: string,
    moduleIds: string[],
): DuplicateModuleNameError {
    return { error: "duplicate_module_name", moduleName, moduleIds };
}

// =============================================================================
// Runtime errors
// =============================================================================

export interface MissingExternalModuleError {
    error: "missing_external_module";
    module: string;
    availableModules: string[];
}

export function missingExternalModule(
    module: string,
    availableModules: string[],
): MissingExternalModuleError {
    return { error: "missing_external_module", module, availableModules };
}

// =============================================================================
// Migration errors
// =============================================================================

export interface MigrationFailedError {
    error: "migration_failed";
    fromVersion: string;
    toVersion: string;
    detail: string;
}

export interface UnsupportedSchemaVersionError {
    error: "unsupported_schema_version";
    version: string;
    supportedRange: { min: string; max: string };
}

// =============================================================================
// Migration error constructors
// =============================================================================

export function migrationFailed(
    fromVersion: string,
    toVersion: string,
    detail: string,
): MigrationFailedError {
    return { error: "migration_failed", fromVersion, toVersion, detail };
}

export function unsupportedSchemaVersion(
    version: string,
    supportedRange: { min: string; max: string },
): UnsupportedSchemaVersionError {
    return { error: "unsupported_schema_version", version, supportedRange };
}

// =============================================================================
// Capability errors
// =============================================================================

export interface CapabilityMissingError {
    error: "capability_missing";
    nodeId: string | null;
    required: string[];      // permissions required by the callee
    available: string[];     // permissions available at the call site
    suggestion?: FixSuggestion;
}

/** Emit when a function call requires capability permissions the caller doesn't have. */
export function capabilityMissing(
    nodeId: string | null,
    required: string[],
    available: string[],
    suggestion?: FixSuggestion,
): CapabilityMissingError {
    return { error: "capability_missing", nodeId, required, available, ...(suggestion ? { suggestion } : {}) };
}

// =============================================================================
// Approval errors
// =============================================================================

export interface ApprovalPropagationMissingError {
    error: "approval_propagation_missing";
    nodeId: string | null;
    functionName: string;
    callSiteNodeId: string | null;
    calleeName: string;
    calleeApproval: { scope: string; reason: string };
    suggestion?: FixSuggestion;
}

/** Emit when a function calls an approval-gated callee without being approval-gated itself. */
export function approvalPropagationMissing(
    nodeId: string | null,
    functionName: string,
    callSiteNodeId: string | null,
    calleeName: string,
    calleeApproval: { scope: string; reason: string },
    suggestion?: FixSuggestion,
): ApprovalPropagationMissingError {
    const err: ApprovalPropagationMissingError = {
        error: "approval_propagation_missing",
        nodeId, functionName, callSiteNodeId, calleeName, calleeApproval,
    };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

// =============================================================================
// Tool errors
// =============================================================================

export interface UnknownToolError {
    error: "unknown_tool";
    nodeId: string | null;
    toolName: string;
    registeredTools: string[];
    suggestion?: FixSuggestion;
}

export interface ToolArgMismatchError {
    error: "tool_arg_mismatch";
    nodeId: string | null;
    toolName: string;
    missingArgs: string[];
    extraArgs: string[];
    typeMismatches: { arg: string; expected: TypeExpr; actual: TypeExpr }[];
}

// =============================================================================
// Tool error constructors
// =============================================================================

/** Emit when a tool_call references a tool name not registered in any ToolDef. */
export function unknownTool(
    nodeId: string | null,
    toolName: string,
    registeredTools: string[],
    suggestion?: FixSuggestion,
): UnknownToolError {
    const err: UnknownToolError = { error: "unknown_tool", nodeId, toolName, registeredTools };
    if (suggestion) err.suggestion = suggestion;
    return err;
}

/** Emit when tool_call args don't match the tool's parameter signature. */
export function toolArgMismatch(
    nodeId: string | null,
    toolName: string,
    missingArgs: string[],
    extraArgs: string[],
    typeMismatches: { arg: string; expected: TypeExpr; actual: TypeExpr }[],
): ToolArgMismatchError {
    return { error: "tool_arg_mismatch", nodeId, toolName, missingArgs, extraArgs, typeMismatches };
}

// =============================================================================
// Deploy errors
// =============================================================================

export interface ScaffoldFailedError {
    error: "scaffold_failed";
    reason: string;
}

export interface DeployFailedError {
    error: "deploy_failed";
    code?: string;
    reason?: string;
    responseBody?: string;
}

export interface UnknownDeployTargetError {
    error: "unknown_deploy_target";
    target: string;
    validTargets: string[];
}

// =============================================================================
// Deploy error constructors
// =============================================================================

export function scaffoldFailed(
    reason: string,
): ScaffoldFailedError {
    return { error: "scaffold_failed", reason };
}

export function deployFailed(
    code?: string,
    reason?: string,
    responseBody?: string,
): DeployFailedError {
    const err: DeployFailedError = { error: "deploy_failed" };
    if (code !== undefined) err.code = code;
    if (reason !== undefined) err.reason = reason;
    if (responseBody !== undefined) err.responseBody = responseBody;
    return err;
}

export function unknownDeployTarget(
    target: string,
    validTargets: string[],
): UnknownDeployTargetError {
    return { error: "unknown_deploy_target", target, validTargets };
}

// =============================================================================
// QuickJS runtime errors
// =============================================================================

export interface QuickJSRuntimeError {
    error: "quickjs_runtime_error";
    message: string;
}

export function quickjsRuntimeError(
    message: string,
): QuickJSRuntimeError {
    return { error: "quickjs_runtime_error", message };
}
